/**
 * Org-scoped Credits ledger contract (Directory + Platform).
 *
 * Category ids and source systems match Platform `creditLedgerCategories`
 * (docs/CREDITS_LEDGER_API.md). Directory and Platform both read the same
 * append-only ledger via:
 * - GET /api/billing/me/credits?orgId=&month=&view=pool|plan|usage
 * - GET /api/billing/me/credits/ledger?orgId=&month=&limit=&cursor=&category=
 * - GET /api/platform/billing/orgs/:orgId/credits*
 */

/** Day-one usage categories that debit (or adjust) the unified Credits pool. */
export const CREDIT_USAGE_CATEGORIES = [
  "hosted_inbox",
  "storage",
  "write",
  "read",
  "records",
  "automations",
  "email_out",
  "email_in",
  "sms",
  "ai_tokens",
  "workforce_runs",
  "voice",
  "sandbox_copy",
  "sandbox_use",
] as const;

export type CreditUsageCategory = (typeof CREDIT_USAGE_CATEGORIES)[number];

/** ATW systems that may emit into the org Credits ledger (Platform ids). */
export const CREDIT_SOURCE_SYSTEMS = [
  "directory",
  "one",
  "mail",
  "workforce",
  "ai",
  "voice",
  "sms",
  "sandbox",
  "platform",
  "support",
] as const;

export type CreditSourceSystem = (typeof CREDIT_SOURCE_SYSTEMS)[number];

/** Debit = charge against the pool; credit = top-up, grant, or reversal. */
export type CreditLedgerDirection = "debit" | "credit";

/**
 * Related resource ids so a charge is explainable in audit (not only aggregates).
 * Keep values non-secret; prefer public resource ids over tokens.
 */
export type CreditRelatedResource = {
  type: string;
  id: string;
  label?: string;
};

/**
 * Atomic auditable ledger entry. Every Credits charge or credit must be
 * reconstructible from this row: what category, how much quantity, how many
 * Credits, which system, when, who (if any), and related resource ids.
 */
export type CreditLedgerEntry = {
  /** Stable id (UUID or source-system idempotency key). */
  id: string;
  orgId: string;
  direction: CreditLedgerDirection;
  category: CreditUsageCategory;
  /** Raw metered amount in category units (messages, minutes, bytes, tokens, runs). */
  quantity: number;
  /** Credits moved after the org's frozen rate-card weights (always >= 0). */
  credits: number;
  sourceSystem: CreditSourceSystem;
  /** ISO-8601 timestamp when the billable action occurred. */
  occurredAt: string;
  /** Short human explanation suitable for the audit list. */
  summary: string;
  /** Optional Directory user id who triggered the action. */
  actorUserId?: string | null;
  /** Optional display label for audit (email or name). */
  actorLabel?: string | null;
  /** Related records (mailbox, run id, message id, sandbox id, invoice id, …). */
  relatedResources?: CreditRelatedResource[];
  /** Free-form non-PII context (model name, unit hints). */
  metadata?: Record<string, string | number | boolean | null>;
};

/** @deprecated Prefer CreditLedgerEntry; kept as an alias for emitters mid-migration. */
export type CreditUsageEvent = CreditLedgerEntry;

/** Roll-up used by the Billing usage table. */
export type CreditUsageCategoryAggregate = {
  category: CreditUsageCategory;
  quantity: number;
  credits: number;
  eventCount: number;
  sourceSystems: CreditSourceSystem[];
};

export const CREDIT_USAGE_CATEGORY_LABELS: Record<CreditUsageCategory, string> = {
  hosted_inbox: "Hosted inbox (monthly)",
  storage: "Storage",
  write: "Write",
  read: "Read",
  records: "Records",
  automations: "Automations",
  email_out: "Email outbound",
  email_in: "Email inbound",
  sms: "SMS",
  ai_tokens: "AI tokens",
  workforce_runs: "Workforce runs",
  voice: "Voice",
  sandbox_copy: "Sandbox copy",
  sandbox_use: "Sandbox use",
};

/** Platform metric keys used when emitting ledger rows. */
export const CREDIT_USAGE_CATEGORY_METRIC: Record<CreditUsageCategory, string> = {
  hosted_inbox: "mailbox.hosted",
  storage: "storage.bytes",
  write: "api.write",
  read: "api.read",
  records: "record.count.event",
  automations: "automation.run",
  email_out: "email.sent",
  email_in: "email.received",
  sms: "sms.sent",
  ai_tokens: "ai.tokens",
  workforce_runs: "workforce.run",
  voice: "voice.minutes",
  sandbox_copy: "sandbox.copy",
  sandbox_use: "sandbox.active",
};

export const CREDIT_SOURCE_SYSTEM_LABELS: Record<CreditSourceSystem, string> = {
  directory: "Directory",
  one: "ATW One",
  mail: "Mail",
  workforce: "Workforce",
  ai: "ATW AI / Agents",
  voice: "Voice",
  sms: "SMS",
  sandbox: "Sandbox",
  platform: "Platform",
  support: "Support",
};

export function isCreditUsageCategory(value: string): value is CreditUsageCategory {
  return (CREDIT_USAGE_CATEGORIES as readonly string[]).includes(value);
}

export function isCreditSourceSystem(value: string): value is CreditSourceSystem {
  return (CREDIT_SOURCE_SYSTEMS as readonly string[]).includes(value);
}

export function isCreditLedgerDirection(value: string): value is CreditLedgerDirection {
  return value === "debit" || value === "credit";
}
